<template>
  <span class="task-center-root">
    <el-popover
    v-model="visible"
    placement="bottom-end"
    width="390"
    trigger="hover"
    popper-class="task-center-popover"
    @show="resetList"
    @hide="stopPolling"
  >
    <section class="task-center-panel" :aria-label="$t('header.taskCenter.title')">
      <header class="task-center-header">
        <h2>{{ $t("header.taskCenter.title") }}</h2>
        <button type="button" :aria-label="$t('header.taskCenter.close')" @click="visible = false">
          <i class="el-icon-close"></i>
        </button>
      </header>

      <div class="task-center-status" role="tablist">
        <button
          v-for="status in statuses"
          :key="status"
          type="button"
          role="tab"
          :aria-selected="activeStatus === status"
          :class="{ active: activeStatus === status }"
          @click="selectStatus(status)"
        >
          {{ $t(`header.taskCenter.status.${status}`) }}
        </button>
      </div>

      <div class="task-center-types" role="group" :aria-label="$t('header.taskCenter.typeLabel')">
        <button
          v-for="type in types"
          :key="type"
          type="button"
          :class="{ active: activeType === type }"
          @click="selectType(type)"
        >
          <i :class="type === 'image' ? 'el-icon-picture-outline' : 'el-icon-video-camera'"></i>
          {{ $t(`header.taskCenter.type.${type}`) }}
        </button>
      </div>

      <div ref="list" class="task-center-list" @scroll="loadOnScroll">
        <article v-for="task in tasks" :key="task.taskId" class="task-center-item">
          <component
            :is="task.status === 'failed' ? 'button' : 'div'"
            class="task-center-summary"
            :class="{ 'is-expandable': task.status === 'failed' }"
            :type="task.status === 'failed' ? 'button' : null"
            :aria-expanded="task.status === 'failed' ? expandedTaskId === task.taskId : null"
            @click="toggleTask(task)"
          >
            <div class="task-center-copy">
              <strong :title="task.taskName">{{ task.taskName || "-" }}</strong>
              <el-tooltip :content="task.scene || '-'" placement="top" :open-delay="300">
                <span>{{ task.scene || "-" }}</span>
              </el-tooltip>
            </div>
            <span class="task-center-time">{{ taskTime(task) }}</span>
            <span class="task-center-points">
              {{ task.points != null ? $t("header.taskCenter.points", { points: task.points }) : "-" }}
            </span>
            <span class="task-center-status-cell">
              <span
                class="task-center-result"
                :class="`is-${task.status}`"
                role="img"
                :aria-label="$t(`header.taskCenter.status.${task.status}`)"
              ></span>
              <i
                v-if="task.status === 'failed'"
                class="task-center-chevron"
                :class="expandedTaskId === task.taskId ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"
              ></i>
            </span>
          </component>
          <div v-if="expandedTaskId === task.taskId" class="task-center-error" @click.stop>
            <div>
              <span>{{ $t("header.taskCenter.failureReason") }}</span>
              <strong>{{ task.failureReason || $t("header.taskCenter.timeoutReason") }}</strong>
            </div>
            <button type="button" @click="openReport(task)">
              {{ $t("header.taskCenter.reportError") }}
            </button>
          </div>
        </article>
        <div class="task-center-footer" role="status">
          <template v-if="loading">{{ $t("common.loading") }}</template>
          <template v-else-if="loadFailed">{{ $t("header.taskCenter.loadFailed") }}</template>
          <template v-else-if="!tasks.length">{{ $t("header.taskCenter.empty") }}</template>
          <template v-else>{{ $t(hasMore ? "header.taskCenter.scrollForMore" : "header.taskCenter.allLoaded") }}</template>
        </div>
      </div>
    </section>

    <button
      slot="reference"
      type="button"
      class="task-center-trigger"
      :aria-label="$t('header.taskCenter.open')"
      :aria-expanded="visible"
    >
      <i class="el-icon-s-order"></i>
      <span v-if="runningCount > 0" class="task-center-badge">
        {{ runningCount > 99 ? "99+" : runningCount }}
      </span>
    </button>
    </el-popover>

    <el-dialog
      :visible.sync="reportVisible"
      width="39.28571rem"
      :show-close="false"
      append-to-body
      custom-class="task-report-dialog"
      @click.native.stop
      @closed="resetReport"
    >
      <form class="task-report-form" @submit.prevent="submitReport">
        <header class="task-report-header">
          <div>
            <h2>{{ $t("header.taskCenter.report.title") }}</h2>
            <p>
              {{
                $t("header.taskCenter.report.subtitle", {
                  task: selectedTaskTitle,
                  points: selectedTask ? selectedTask.points : 0,
                })
              }}
            </p>
          </div>
          <button
            type="button"
            :aria-label="$t('header.taskCenter.report.close')"
            @click="reportVisible = false"
          >
            <i class="el-icon-close"></i>
          </button>
        </header>

        <label class="task-report-field">
          <span>{{ $t("header.taskCenter.report.summary") }}</span>
          <input :value="selectedTaskFailureReason" readonly />
        </label>

        <label class="task-report-field">
          <span>
            {{ $t("header.taskCenter.report.description") }}
            <i>*</i>
          </span>
          <span class="task-report-textarea">
            <textarea
              ref="reportDescription"
              v-model="reportDescription"
              maxlength="200"
              :aria-invalid="reportDescriptionError"
              :class="{ 'is-error': reportDescriptionError }"
              :placeholder="$t('header.taskCenter.report.placeholder')"
              @input="reportDescriptionError = false"
            ></textarea>
            <small>{{ reportDescription.length }}/200</small>
          </span>
          <span v-if="reportDescriptionError" class="task-report-error">
            {{ $t("header.taskCenter.report.descriptionRequired") }}
          </span>
        </label>

        <footer class="task-report-actions">
          <button type="button" @click="reportVisible = false">
            {{ $t("header.taskCenter.report.cancel") }}
          </button>
          <button type="submit" class="primary" :disabled="reportSubmitting">
            {{
              reportSubmitting
                ? $t("header.feedbackDialog.submitting")
                : $t("header.taskCenter.report.submit")
            }}
          </button>
        </footer>
      </form>
    </el-dialog>
  </span>
</template>

<script>
const PAGE_SIZE = 5;
const POLL_INTERVAL = 5000;

export default {
  name: "TaskCenter",
  props: {
    runningCount: { type: Number, default: 0 },
  },
  data() {
    return {
      visible: false,
      activeStatus: "all",
      activeType: "video",
      expandedTaskId: null,
      reportVisible: false,
      selectedTask: null,
      reportDescription: "",
      reportDescriptionError: false,
      reportSubmitting: false,
      pageNum: 0,
      pages: 0,
      loading: false,
      loadFailed: false,
      requestId: 0,
      pollTimer: null,
      polling: false,
      statuses: ["all", "running", "success", "failed"],
      types: ["video", "image"],
      tasks: [],
    };
  },
  computed: {
    selectedTaskTitle() {
      return this.selectedTask ? this.selectedTask.taskName : "";
    },
    selectedTaskFailureReason() {
      return this.selectedTask && this.selectedTask.failureReason
        ? this.selectedTask.failureReason
        : this.$t("header.taskCenter.timeoutReason");
    },
    hasMore() {
      return this.pageNum < this.pages;
    },
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    resetList() {
      this.stopPolling();
      this.expandedTaskId = null;
      this.$nextTick(() => {
        if (this.$refs.list) this.$refs.list.scrollTop = 0;
      });
      this.fetchTasks(true);
    },
    selectStatus(status) {
      this.activeStatus = status;
      this.resetList();
    },
    selectType(type) {
      this.activeType = type;
      this.resetList();
    },
    loadOnScroll({ currentTarget }) {
      if (!this.loading && this.hasMore && currentTarget.scrollTop + currentTarget.clientHeight >= currentTarget.scrollHeight - 8) {
        this.fetchTasks();
      }
    },
    toggleTask(task) {
      if (task.status === "failed") {
        this.expandedTaskId = this.expandedTaskId === task.taskId ? null : task.taskId;
      }
    },
    taskTime(task) {
      const match = String(task.createTime || "").match(/^\d{4}-(\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})/);
      return match ? `${match[1]} ${match[2]}` : "-";
    },
    async fetchTasks(reset = false) {
      const requestId = ++this.requestId;
      const pageNum = reset ? 1 : this.pageNum + 1;
      if (reset) {
        this.tasks = [];
        this.pageNum = 0;
        this.pages = 0;
      }
      this.loading = true;
      this.loadFailed = false;
      try {
        const response = await this.$axios.get(`${this.$store.state.serverUrl}ytb/taskCenter/page`, {
          params: {
            pageNum,
            pageSize: PAGE_SIZE,
            status: this.activeStatus,
            type: this.activeType,
          },
        });
        if (requestId !== this.requestId) return;
        const result = response.data || {};
        if (Number(result.code) !== 200) throw new Error(result.message);
        const page = result.data || {};
        const list = Array.isArray(page.list) ? page.list : [];
        this.tasks = reset ? list : this.tasks.concat(list);
        this.pageNum = Number(page.pageNum) || pageNum;
        this.pages = Number(page.pages) || 0;
        this.syncPolling();
      } catch (error) {
        if (requestId === this.requestId) this.loadFailed = true;
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    syncPolling() {
      if (!this.visible || !this.tasks.some((task) => task.status === "running")) {
        this.stopPolling();
        return;
      }
      if (!this.pollTimer) {
        this.pollTimer = setInterval(this.pollRunningTasks, POLL_INTERVAL);
      }
    },
    stopPolling() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.pollTimer = null;
      this.polling = false;
    },
    async pollRunningTasks() {
      if (this.polling || this.loading || !this.visible) return;
      const requestId = this.requestId;
      const activeType = this.activeType;
      this.polling = true;
      try {
        const response = await this.$axios.get(`${this.$store.state.serverUrl}ytb/taskCenter/page`, {
          params: {
            pageNum: 1,
            pageSize: Math.min(Math.max(this.tasks.length, PAGE_SIZE), 100),
            status: "all",
            type: activeType,
          },
        });
        if (!this.visible || requestId !== this.requestId || activeType !== this.activeType) return;
        const result = response.data || {};
        if (Number(result.code) !== 200) return;
        const latestTasks = new Map(
          ((result.data && result.data.list) || []).map((task) => [String(task.taskId), task]),
        );
        this.tasks = this.tasks.map((task) => latestTasks.get(String(task.taskId)) || task);
        this.syncPolling();
      } catch (error) {
        // 瞬时失败不终止轮询，下个周期继续刷新。
      } finally {
        this.polling = false;
      }
    },
    openReport(task) {
      this.selectedTask = task;
      this.reportDescription = "";
      this.reportDescriptionError = false;
      this.reportVisible = true;
    },
    async submitReport() {
      if (!this.reportDescription.trim()) {
        this.reportDescriptionError = true;
        this.$nextTick(() => this.$refs.reportDescription.focus());
        return;
      }
      if (this.reportSubmitting || !this.selectedTask) return;
      this.reportSubmitting = true;
      try {
        const response = await this.$axios.post(
          `${this.$store.state.serverUrl}ytb/workOrder/aiTask/create`,
          {
            taskId: String(this.selectedTask.taskId),
            problemDescription: this.reportDescription.trim(),
          },
        );
        const result = response.data || {};
        if (Number(result.code) !== 200) throw new Error(result.message);
        this.$message.success(this.$t("header.feedbackDialog.submitSuccess"));
        this.reportVisible = false;
      } catch (error) {
        this.$message.error(error.message || this.$t("header.feedbackDialog.submitFailed"));
      } finally {
        this.reportSubmitting = false;
      }
    },
    resetReport() {
      this.selectedTask = null;
      this.reportDescription = "";
      this.reportDescriptionError = false;
      this.reportSubmitting = false;
    },
  },
};
</script>

<style lang="less">
.task-center-root { display: inline-flex; }
.task-center-trigger {
  position: relative;
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  width: 2.28571rem;
  height: 2.28571rem;
  padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 0.42857rem;
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  cursor: pointer;
  &:hover, &:focus-visible { border-color: #8b5cf6; outline: none; }
  > i { font-size: ~"max(15PX, 1.14286rem)"; }
}
.task-center-badge {
  position: absolute;
  top: -0.5rem;
  right: -0.5rem;
  min-width: 1.28571rem;
  height: 1.28571rem;
  padding: 0 0.28571rem;
  border-radius: 0.71429rem;
  background: #8b5cf6;
  font-size: ~"max(10PX, 0.71429rem)";
  font-weight: 600;
  line-height: 1.28571rem;
}
.task-center-popover.el-popover {
  box-sizing: border-box;
  padding: 0;
  overflow: hidden;
  border: 1px solid #32333c;
  border-radius: 0.57143rem;
  background: #17181e;
  box-shadow: 0 1.57143rem 4.28571rem rgba(0, 0, 0, 0.58);
  color: #eef0f7;
}
.task-center-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.14286rem 1.42857rem 0.57143rem;
  h2 { margin: 0; font-size: ~"max(18PX, 1.28571rem)"; letter-spacing: 0; }
  button { display: grid; place-items: center; width: 2rem; height: 2rem; padding: 0; border: 0; background: transparent; color: #9da1ae; cursor: pointer; }
}
.task-center-status {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 0 1rem;
  border-bottom: 1px solid #303137;
  button { position: relative; height: 3.07143rem; padding: 0; border: 0; background: transparent; color: #aeb0bb; font-size: ~"max(12PX, 0.92857rem)"; cursor: pointer; }
  button.active { color: #a873ff; font-weight: 600; }
  button.active::after { position: absolute; right: 20%; bottom: 0; left: 20%; height: 2px; border-radius: 2px; background: #9251ff; content: ""; }
}
.task-center-types {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.42857rem;
  padding: 1.14286rem 1.28571rem 0.85714rem;
  button { height: 2.71429rem; overflow: hidden; border: 1px solid #363741; border-radius: 0.42857rem; background: #18191f; color: #a8aab4; font-size: ~"max(12PX, 0.92857rem)"; text-overflow: ellipsis; white-space: nowrap; cursor: pointer; }
  button.active { border-color: #864bff; background: #211a31; color: #a86fff; }
  i { margin-right: 0.42857rem; }
}
.task-center-list {
  height: 27rem;
  margin-right: 0.42857rem;
  padding: 0 0.71429rem 0 1.28571rem;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-color: #585b68 #202127;
  scrollbar-width: thin;
}
.task-center-item {
  border-bottom: 1px solid #303137;
}
.task-center-summary {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto 1.71429rem;
  align-items: center;
  gap: 0.85714rem;
  width: 100%;
  min-height: 5.28571rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  text-align: left;
  outline: none;
  &.is-expandable { cursor: pointer; }
  &.is-expandable:focus-visible { box-shadow: inset 0 0 0 1px #8b5cf6; }
}
.task-center-copy {
  min-width: 0;
  strong, span { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  strong { margin-bottom: 0.35714rem; color: #f3f3f7; font-size: ~"max(13PX, 1rem)"; }
  span { color: #9da0aa; font-size: ~"max(11PX, 0.78571rem)"; }
}
.task-center-time { color: #9da0aa; font-size: ~"max(11PX, 0.78571rem)"; white-space: nowrap; }
.task-center-points { color: #9f68ff; font-size: ~"max(11PX, 0.85714rem)"; white-space: nowrap; }
.task-center-status-cell {
  position: relative;
  align-self: stretch;
  width: 1.71429rem;
}
.task-center-result {
  position: absolute;
  right: 0;
  bottom: 0.71429rem;
  display: block;
  width: 0.57143rem;
  height: 0.57143rem;
  box-sizing: border-box;
  border-radius: 50%;
  &.is-success { background: #21b760; }
  &.is-failed { background: #ef4339; }
  &.is-running { background: #8f4fff; }
}
.task-center-chevron { position: absolute; bottom: 0.21429rem; left: 50%; color: #777b88; font-size: ~"max(10PX, 0.71429rem)"; transform: translateX(-50%); }
.task-center-error {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.85714rem;
  margin-bottom: 1rem;
  padding: 0.85714rem;
  border: 1px solid #35363f;
  border-radius: 0.42857rem;
  background: #1b1c22;
  cursor: default;
  div { min-width: 0; }
  span, strong { display: block; }
  span { margin-bottom: 0.42857rem; color: #9fa2ac; font-size: ~"max(11PX, 0.78571rem)"; }
  strong { color: #f3f3f7; font-size: ~"max(12PX, 0.85714rem)"; }
  button { flex: 0 0 auto; min-width: 6.85714rem; height: 2.28571rem; border: 1px solid #8b50ff; border-radius: 0.42857rem; background: #221832; color: #b37aff; cursor: pointer; }
  button:hover, button:focus-visible { border-color: #a873ff; color: #c7a2ff; outline: none; }
}
.task-center-footer { height: 2.57143rem; color: #838691; font-size: ~"max(11PX, 0.78571rem)"; line-height: 2.57143rem; text-align: center; }
.task-report-dialog.el-dialog {
  max-width: calc(100vw - 2rem);
  overflow: hidden;
  border: 1px solid #41434d;
  border-radius: 0.71429rem;
  background: #202127;
  box-shadow: 0 2rem 5.71429rem rgba(0, 0, 0, 0.72);
  color: #eef0f7;
  .el-dialog__header { display: none; }
  .el-dialog__body { padding: 0; color: inherit; }
}
.task-report-form { padding: 1.71429rem; }
.task-report-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.71429rem;
  h2 { margin: 0 0 0.57143rem; font-size: ~"max(20PX, 1.57143rem)"; letter-spacing: 0; }
  p { margin: 0; color: #d0d1d8; font-size: ~"max(12PX, 0.92857rem)"; }
  button { display: grid; flex: 0 0 2.28571rem; place-items: center; width: 2.28571rem; height: 2.28571rem; padding: 0; border: 0; background: transparent; color: #9da1ae; font-size: 1.14286rem; cursor: pointer; }
}
.task-report-field {
  display: block;
  margin-bottom: 1.42857rem;
  > span:first-child { display: block; margin-bottom: 0.57143rem; color: #e8e9ee; font-size: ~"max(12PX, 0.92857rem)"; font-weight: 600; }
  i { color: #f05a57; font-style: normal; }
  input, textarea { box-sizing: border-box; width: 100%; border: 1px solid #444650; border-radius: 0.42857rem; outline: none; background: #2a2b32; color: #f2f3f5; font: inherit; }
  input { height: 3.14286rem; padding: 0 1rem; }
  textarea { min-height: 8rem; padding: 0.92857rem 1rem 2rem; resize: vertical; }
  input:focus, textarea:focus { border-color: #8251eb; box-shadow: 0 0 0 3px rgba(129, 80, 237, 0.15); }
}
.task-report-textarea {
  position: relative;
  display: block;
  small { position: absolute; right: 0.71429rem; bottom: 0.71429rem; color: #8f929d; font-size: ~"max(10PX, 0.71429rem)"; }
  textarea.is-error, textarea.is-error:focus { border-color: #f05a57; box-shadow: none; }
}
.task-report-error { display: block; margin-top: 0.42857rem; color: #f05a57; font-size: ~"max(12PX, 0.85714rem)"; }
.task-report-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.85714rem;
  button { min-width: 8.42857rem; height: 2.85714rem; border: 1px solid #484a54; border-radius: 0.42857rem; background: #27282e; color: #d3d4da; font: inherit; cursor: pointer; }
  button.primary { border-color: #8150ed; background: #8150ed; color: #fff; font-weight: 600; }
  button:hover, button:focus-visible { border-color: #9b6cff; outline: none; }
}
@media (max-width: 760px) {
  .task-center-popover.el-popover { width: calc(100vw - 1.71429rem) !important; max-width: 27.85714rem; }
  .task-center-list { height: min(27rem, calc(100vh - 15rem)); }
  .task-report-dialog.el-dialog { width: calc(100vw - 1.71429rem) !important; margin-top: 2vh !important; }
  .task-report-form { max-height: 96vh; padding: 1.14286rem; overflow-y: auto; }
  .task-report-actions button { min-width: 0; flex: 1 1 0; }
}
</style>
